import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

import { tap, map, catchError, switchMap, retry } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { Staff, CrudListResponses, CrudSuccessResponse } from '@app/shared/models';
import swal from 'sweetalert2';
import { AuthService } from '@app/shared/auth/auth.service';
import { GetAPIResponse } from '@app/shared/models/crud-responses';
import { AccountAccessGuardService } from '../helpers/account-access-guard.service';

@Injectable({
  providedIn: 'root'
})
export class StaffService {
  apiv2 = environment.apiUrlV2;
  private _staff: Staff[] = [];
  private readonly _record = new BehaviorSubject<GetAPIResponse>({});
  public record$ = this._record.asObservable();
  params: any = {
    per_page: 10,
    page: 1,
    total: 0
  };

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private accountAccessGuardService: AccountAccessGuardService
    ) {
    this.getInitialRecord();
  }
  setParams(params: any) {
    this.params = params;
  }
  refreshList() {
    this._record.next(null);
    this.getInitialRecord(this.params).subscribe();
  }
  getInitialRecord(params?: any) {
    return this.http.get(`${this.apiv2}/accounts/staff`, { params: params }).pipe(
      tap((data: GetAPIResponse) => {
        this._record.next(data);
        this._staff = <Staff[]>data.data;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }


  getById(id: number): Observable<Staff> {
    return this.getStaffFromApi(id);
  }
  // deprecated method
  get(id: number): Observable<Staff> {
    return this.getStaffFromApi(id);
  }
  // end of get method

  private getStaffFromApi(id: number): Observable<Staff> {
    return this.http.get<Staff>(`${this.apiv2}/accounts/staff/${id}`);
  }

  createRecord(data: any): Observable<Staff> {

    return this.accountAccessGuardService.accountGuardRestriction(() => this.http.post(`${this.apiv2}/accounts/staff`, data).pipe(
      tap(
        (res: any) => {
          if (res.id < 0) {
            return of({});
          }
          data.id = res.id;
          if (res.accountId) {
            data.account_d = res.accountId;
          }
          if (res.status) {
            data.status = res.status;
          }
          if (res.loginId) {
            data.login_id = res.loginId;
          }
          this._staff.push(<Staff>data);
          return of(<Staff>data);
        },
        (error) => {
          swal({
            title: 'Save error!',
            text: error.error.error,
            type: 'warning',
            showCancelButton: false,
            confirmButtonColor: '#3085d6'
          });
          return of(null);
        }
      )
    ));
  }

  updateRecord(id: number, data: any): Observable<Staff> {

    return this.accountAccessGuardService.accountGuardRestriction(() => this.http.put(`${this.apiv2}/accounts/staff/${id}`, data).pipe(
      tap(
        (res: any) => {
          let staff = this._staff.find((row) => row.id == id);
          if (staff) {
            staff = <Staff>Object.assign(staff, data);
          }
        },
        (error) => {
          swal({
            title: 'Update error!',
            text: error.error.message,
            type: 'warning',
            showCancelButton: false,
            confirmButtonColor: '#3085d6'
          });
          return of(null);
        }
      )
    ));
  }


  /**
   * Changes a staff member's sign-in email.
   *
   * Kept apart from updateRecord() because the email is the login identity: the API validates
   * it against every login in the tenant and answers 409 when the address is already taken.
   */
  updateEmail(id: number, newEmail: string): Observable<any> {
    return this.accountAccessGuardService.accountGuardRestriction(() =>
      this.http.put(`${this.apiv2}/accounts/staff/${id}/email`, { new_email: newEmail })
    );
  }

  updateStaffStatus(id: number): Observable<any> {

    let staff = this._staff.find((row) => row.id == id);
    let data = { status: staff.status == 0 ? 1 : 0 };

    return this.accountAccessGuardService.accountGuardRestriction(() => this.http.put(`${this.apiv2}/accounts/staff/${id}/status`, data).pipe(map(() => true)));
  }
  // deprecated method
  activate(id: number): Observable<any> {
    let staff = this._staff.find((row) => row.id == id);
    let data = { status: staff.status == 0 ? 1 : 0, account_id: staff.accountId };
    return this.updateRecord(id, data).pipe(map(() => true));
  }
  // end of activate method
  getClinics(id: number) {
    return this.http.get(`${this.apiv2}/accounts/staff/${id}/clinics`);
  }

  getLaboratories(id: number) {
    return this.http.get(`${this.apiv2}/accounts/staff/${id}/laboratories`);
  }

  deleteRecord(id: number): Observable<boolean> {

    return this.accountAccessGuardService.accountGuardRestriction(() => this.http.delete(`${this.apiv2}/accounts/staff/${id}`).pipe(
      tap((data) => {
        const staff = this._staff;
        const assIdx: number = staff.findIndex((row) => row.id == id);
        staff.splice(assIdx, 1);
      }),
      map(() => true),
      catchError((error) => {
        swal({
          title: 'Delete error!',
          text: error.error,
          type: 'warning',
          showCancelButton: false,
          confirmButtonColor: '#3085d6'
        });
        return throwError(error);
      })
    ));
  }

}
